import { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import type { MenuItem, MenuVariant, MenuAddon, Category } from '@/lib/data';
import { fetchMenuBySlug, placeSelfOrder, fetchQrOrderingEnabled } from '@/lib/data';
import WelcomeScreen from './components/WelcomeScreen';
import MenuScreen from './components/MenuScreen';
import ItemDetailModal from './components/ItemDetailModal';
import CartPanel from './components/CartPanel';
import OrderConfirmed from './components/OrderConfirmed';
import VoiceAssistant from './components/VoiceAssistant';

export interface CartItem {
  menuItem: MenuItem;
  selectedVariant: MenuVariant | null;
  selectedAddons: MenuAddon[];
  quantity: number;
  specialInstructions: string;
}

type Screen = 'welcome' | 'menu' | 'confirmed';

export default function SelfOrderPage() {
  const { outletSlug, tableCode } = useParams<{ outletSlug: string; tableCode: string }>();
  const [screen, setScreen] = useState<Screen>('welcome');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [orderId, setOrderId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [orderError, setOrderError] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [voiceOpen, setVoiceOpen] = useState(false);

  // Live data from Supabase
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [restaurantId, setRestaurantId] = useState('');
  const [restaurantName, setRestaurantName] = useState('');
  const [gstRate, setGstRate] = useState(0);
  const [gstInclusive, setGstInclusive] = useState(false);
  const [channels, setChannels] = useState<Record<string, boolean> | null>(null);
  const [tableValid, setTableValid] = useState(true);
  // Plan gate: when the restaurant's plan is below Growth, the page is
  // BROWSE-ONLY (menu visible, ordering disabled). Default true so the menu
  // shows immediately; flipped to false once the public gate RPC resolves.
  const [qrOrderingAllowed, setQrOrderingAllowed] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!outletSlug) {
      setLoadError('This QR link is missing the outlet.');
      setLoading(false);
      return;
    }
    setLoading(true);
    // Resolve the plan gate in parallel (anonymous-safe). Browse-only if false.
    fetchQrOrderingEnabled(outletSlug)
      .then((ok) => setQrOrderingAllowed(ok))
      .catch(() => setQrOrderingAllowed(false));
    fetchMenuBySlug(outletSlug)
      .then((res) => {
        setRestaurantId(res.restaurant.id);
        setRestaurantName(res.restaurant.name);
        setGstRate(res.restaurant.gstRate);
        setGstInclusive(res.restaurant.gstInclusive ?? false);
        setChannels(res.restaurant.channels ?? null);
        setCategories(res.categories);
        setMenu(res.menu);
        // No tableCode (public /r/{slug} link) = browse mode, always valid.
        setTableValid(
          !tableCode ||
            res.tables.some((t) => t.id === tableCode || t.slug === tableCode),
        );
        setLoadError('');
      })
      .catch((e: unknown) =>
        setLoadError(e instanceof Error ? e.message : 'Could not load the menu.'))
      .finally(() => setLoading(false));
  }, [outletSlug, tableCode]);

  /* ── Guest session ──────────────────────────────────────────────────────
     Kept in sessionStorage on purpose: it survives a refresh / backgrounding
     (so the diner never loses their live order status) but is discarded the
     moment they close the tab — no stale order tracker for the next guest on
     the same phone. */
  const sessionKey = `ezydine.selforder:${outletSlug ?? ''}:${tableCode ?? 'browse'}`;

  const saveSession = useCallback(
    (patch: Partial<{ orderId: string; accessToken: string; name: string; phone: string; cart: CartItem[]; screen: Screen }>) => {
      try {
        const prev = JSON.parse(sessionStorage.getItem(sessionKey) ?? '{}');
        sessionStorage.setItem(sessionKey, JSON.stringify({ ...prev, ...patch }));
      } catch { /* storage unavailable / quota — non-fatal */ }
    },
    [sessionKey],
  );

  // Restore a live session on mount (refresh / returning to the tab).
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(sessionKey);
      if (!raw) return;
      const s = JSON.parse(raw) as {
        orderId?: string; accessToken?: string; name?: string;
        phone?: string; cart?: CartItem[]; screen?: Screen;
      };
      if (s.name) setCustomerName(s.name);
      if (s.phone) setCustomerPhone(s.phone);
      if (s.screen === 'confirmed' && s.orderId && s.accessToken) {
        setOrderId(s.orderId);
        setAccessToken(s.accessToken);
        setCart(s.cart ?? []);
        setScreen('confirmed');
      } else if (s.screen === 'menu' && s.name) {
        setCart(s.cart ?? []);
        setScreen('menu');
      }
    } catch { /* corrupt payload — ignore and start fresh */ }
  }, [sessionKey]);

  const handleStartOrdering = useCallback((name: string, phone: string, voice: boolean) => {
    setCustomerName(name.trim());
    setCustomerPhone(phone.trim());
    setScreen('menu');
    saveSession({ name: name.trim(), phone: phone.trim(), screen: 'menu' });
    // Voice ordering adds to cart, so it is unavailable in browse-only mode.
    if (voice && qrOrderingAllowed) setVoiceOpen(true);
  }, [qrOrderingAllowed, saveSession]);

  // Voice assistant cart helpers (no variant/addons — quick conversational add).
  const voiceAddItem = useCallback((item: MenuItem, qty: number) => {
    setCart((prev) => {
      const idx = prev.findIndex((ci) => ci.menuItem.id === item.id && !ci.selectedVariant);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + qty };
        return updated;
      }
      return [...prev, { menuItem: item, selectedVariant: null, selectedAddons: [], quantity: qty, specialInstructions: '' }];
    });
  }, []);

  const voiceSetQty = useCallback((item: MenuItem, qty: number) => {
    setCart((prev) => {
      const idx = prev.findIndex((ci) => ci.menuItem.id === item.id);
      if (idx < 0) return qty > 0 ? [...prev, { menuItem: item, selectedVariant: null, selectedAddons: [], quantity: qty, specialInstructions: '' }] : prev;
      const updated = [...prev];
      if (qty <= 0) updated.splice(idx, 1);
      else updated[idx] = { ...updated[idx], quantity: qty };
      return updated;
    });
  }, []);

  const voiceRemoveItem = useCallback((itemId: string) => {
    setCart((prev) => prev.filter((ci) => ci.menuItem.id !== itemId));
  }, []);

  const handleItemClick = useCallback((item: MenuItem) => {
    setSelectedItem(item);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedItem(null);
  }, []);

  const getExistingCartItem = (item: MenuItem): CartItem | undefined => {
    const variantKey = item.variants.length > 0 ? item.variants[0].name : 'novar';
    return cart.find((ci) => {
      const ciVariantKey = ci.selectedVariant?.name || 'novar';
      return ci.menuItem.id === item.id && ciVariantKey === variantKey;
    });
  };

  const handleAddToCart = useCallback((cartItem: CartItem) => {
    if (!qrOrderingAllowed) return; // browse-only: ignore adds
    setCart((prev) => {
      const ciVariantKey = cartItem.selectedVariant?.name || 'novar';
      const existingIdx = prev.findIndex((ci) => {
        const existingVariantKey = ci.selectedVariant?.name || 'novar';
        return ci.menuItem.id === cartItem.menuItem.id && existingVariantKey === ciVariantKey;
      });

      if (existingIdx >= 0) {
        const updated = [...prev];
        updated[existingIdx] = cartItem;
        return updated;
      }
      return [...prev, cartItem];
    });
  }, []);

  const handleUpdateQuantity = useCallback(
    (itemId: string, variantName: string | null, delta: number) => {
      setCart((prev) => {
        const idx = prev.findIndex((ci) => {
          const ciVariantKey = ci.selectedVariant?.name || 'novar';
          return ci.menuItem.id === itemId && ciVariantKey === (variantName || 'novar');
        });
        if (idx < 0) return prev;
        const updated = [...prev];
        const newQty = updated[idx].quantity + delta;
        if (newQty <= 0) {
          updated.splice(idx, 1);
        } else {
          updated[idx] = { ...updated[idx], quantity: newQty };
        }
        return updated;
      });
    },
    [],
  );

  const handleRemoveItem = useCallback((itemId: string, variantName: string | null) => {
    setCart((prev) =>
      prev.filter((ci) => {
        const ciVariantKey = ci.selectedVariant?.name || 'novar';
        return !(ci.menuItem.id === itemId && ciVariantKey === (variantName || 'novar'));
      }),
    );
  }, []);

  // Channel gating: a QR/table order needs QR + Dine-in on; a link (browse)
  // order needs Website on. Missing flag = on (back-compat). Server enforces
  // this too — this is just so guests see it before reaching checkout.
  const orderingPaused = (() => {
    const ch = channels ?? {};
    const on = (k: string) => ch[k] === undefined ? true : !!ch[k];
    return tableCode ? (!on('QR') || !on('DineIn')) : !on('Website');
  })();

  const handlePlaceOrder = useCallback(async () => {
    if (!restaurantId) return;
    if (!qrOrderingAllowed) {
      setOrderError('Online ordering is not available at this outlet. Please order with a member of staff.');
      return;
    }
    if (orderingPaused) {
      setOrderError('Online ordering is currently paused at this outlet. Please ask a member of staff.');
      return;
    }
    setOrderError('');
    // Send only ids/qty/variant/addons — the server recomputes prices & totals
    // (the browser can't set amounts or mark the order paid).
    const items = cart.map((ci) => ({
      id: ci.menuItem.id,
      qty: ci.quantity,
      variant: ci.selectedVariant?.name ?? null,
      addons: ci.selectedAddons.map((a) => a.name),
      specialInstructions: ci.specialInstructions || undefined,
    }));

    try {
      const { orderId, accessToken } = await placeSelfOrder({
        restaurantId,
        tableId: tableCode ?? null,
        items,
        customerName,
        customerPhone,
      });
      setOrderId(orderId);
      setAccessToken(accessToken);
      setShowCart(false);
      setScreen('confirmed');
      // Persist so a refresh keeps the live tracker (until the tab is closed).
      saveSession({ orderId, accessToken, name: customerName, phone: customerPhone, cart, screen: 'confirmed' });
    } catch (e) {
      setOrderError(
        e instanceof Error && /paused/i.test(e.message)
          ? e.message
          : 'Could not place the order. Please check your connection and try again.',
      );
    }
  }, [cart, restaurantId, tableCode, orderingPaused, qrOrderingAllowed, customerName, customerPhone, saveSession]);

  // "Order more" — back to the menu with the diner's details kept, so they
  // don't retype them. The previous order stays live on the kitchen side.
  const handleBackToMenu = useCallback(() => {
    setCart([]);
    setScreen('menu');
    setOrderId('');
    setAccessToken('');
    saveSession({ orderId: '', accessToken: '', cart: [], screen: 'menu' });
  }, [saveSession]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 px-5 py-8 max-w-md mx-auto animate-pulse space-y-4">
        <div className="h-16 w-16 mx-auto rounded-2xl bg-background-200" />
        <div className="h-6 w-2/3 mx-auto rounded bg-background-200" />
        <div className="h-4 w-1/2 mx-auto rounded bg-background-200" />
        <div className="space-y-3 pt-6">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-28 w-full rounded-xl bg-background-100 border border-background-200" />
          ))}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen grid place-items-center bg-background-50 px-6 text-center">
        <div>
          <i className="ri-wifi-off-line text-4xl text-foreground-300" />
          <p className="mt-3 font-semibold text-foreground-900">Couldn't load the menu</p>
          <p className="mt-1 text-sm text-foreground-500">{loadError}</p>
        </div>
      </div>
    );
  }

  if (!tableValid) {
    return (
      <div className="min-h-screen grid place-items-center bg-background-50 px-6 text-center">
        <div>
          <i className="ri-qr-code-line text-4xl text-foreground-300" />
          <p className="mt-3 font-semibold text-foreground-900">Table not found</p>
          <p className="mt-1 text-sm text-foreground-500">
            We couldn't find table "{tableCode}" at {restaurantName}. Please re-scan the QR code on your table or ask a member of staff.
          </p>
        </div>
      </div>
    );
  }

  if (orderingPaused) {
    return (
      <div className="min-h-screen grid place-items-center bg-background-50 px-6 text-center">
        <div>
          <i className="ri-pause-circle-line text-4xl text-foreground-300" />
          <p className="mt-3 font-semibold text-foreground-900">Online ordering is paused</p>
          <p className="mt-1 text-sm text-foreground-500">
            {restaurantName} isn't taking online orders right now. Please ask a member of staff to place your order.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {screen === 'welcome' && (
        <WelcomeScreen
          tableCode={tableCode}
          outletName={restaurantName}
          gstInclusive={gstInclusive}
          onStartOrdering={handleStartOrdering}
        />
      )}

      {screen === 'menu' && (
        <MenuScreen
          menuItems={menu}
          categories={categories}
          cart={cart}
          onItemClick={handleItemClick}
          onCartClick={() => setShowCart(true)}
          browseOnly={!qrOrderingAllowed}
        />
      )}

      {screen === 'confirmed' && (
        <OrderConfirmed orderId={orderId} accessToken={accessToken} restaurantId={restaurantId} cart={cart} gstRate={gstRate} gstInclusive={gstInclusive} onBackToMenu={handleBackToMenu} />
      )}

      {voiceOpen && screen === 'menu' && (
        <VoiceAssistant
          name={customerName}
          restaurantName={restaurantName}
          menu={menu}
          cart={cart}
          onAddItem={voiceAddItem}
          onSetQty={voiceSetQty}
          onRemoveItem={voiceRemoveItem}
          onClose={() => setVoiceOpen(false)}
          onCheckout={() => { setVoiceOpen(false); handlePlaceOrder(); }}
        />
      )}

      {/* Modals / Panels */}
      {selectedItem && (
        <ItemDetailModal
          item={selectedItem}
          cartItem={getExistingCartItem(selectedItem)}
          onClose={handleCloseDetail}
          onAddToCart={handleAddToCart}
          browseOnly={!qrOrderingAllowed}
        />
      )}

      {showCart && (
        <CartPanel
          cart={cart}
          gstRate={gstRate}
          gstInclusive={gstInclusive}
          onClose={() => setShowCart(false)}
          onUpdateQuantity={handleUpdateQuantity}
          onRemoveItem={handleRemoveItem}
          onPlaceOrder={handlePlaceOrder}
        />
      )}

      {/* Inline error toast (replaces the old blocking alert()). */}
      {orderError && (
        <div className="fixed inset-x-0 bottom-0 z-[60] flex justify-center px-4 pb-5 pointer-events-none">
          <div
            role="alert"
            className="pointer-events-auto max-w-md w-full flex items-start gap-3 rounded-xl bg-red-600 text-background-50 px-4 py-3 shadow-lg"
          >
            <i className="ri-error-warning-line text-lg shrink-0 mt-0.5"></i>
            <p className="text-sm flex-1">{orderError}</p>
            <button
              onClick={() => setOrderError('')}
              className="shrink-0 text-background-50/80 hover:text-background-50 cursor-pointer"
              aria-label="Dismiss"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
